import { invokeEdgeFunction } from './client';

/**
 * Data module for organization developer API keys.
 * All operations go through the `manage-api-keys` edge function, which
 * attaches the user JWT automatically and enforces admin access server-side.
 */

/**
 * Lists all API keys for the current organization.
 * @returns {Promise<Array>} Array of key objects {id, name, prefix, scopes, createdAt, lastUsedAt, expiresAt, revokedAt}.
 */
const getApiKeys = async () => {
    const data = await invokeEdgeFunction('manage-api-keys', {
        body: { action: 'list' }
    }, {
        mutation: 'getApiKeys',
        userFriendlyMessage: 'Something went wrong loading API keys',
    });

    return data?.keys ?? [];
};

/**
 * Creates a new API key. The raw key is returned exactly once — the caller
 * must surface it to the user immediately (it is never retrievable again).
 * @param {object} params
 * @param {string} params.name - Human-readable name for the key.
 * @param {string[]} params.scopes - Array of scopes (e.g. 'read:people').
 * @param {string|null} [params.expiresAt] - Optional ISO date string for expiration.
 * @returns {Promise<{key: object, rawKey: string}>}
 */
const createApiKey = async ({ name, scopes, expiresAt = null }) => {
    const data = await invokeEdgeFunction('manage-api-keys', {
        body: {
            action: 'create',
            name,
            scopes,
            ...(expiresAt ? { expiresAt } : {}),
        }
    }, {
        mutation: 'createApiKey',
        context: { name, scopes },
        userFriendlyMessage: 'Something went wrong creating the API key',
    });

    return data;
};

/**
 * Revokes an API key. Revoked keys can no longer authenticate.
 * @param {object} params
 * @param {string} params.id - The key id to revoke.
 * @returns {Promise<{ok: boolean}>}
 */
const revokeApiKey = async ({ id }) => {
    const data = await invokeEdgeFunction('manage-api-keys', {
        body: { action: 'revoke', id }
    }, {
        mutation: 'revokeApiKey',
        context: { id },
        userFriendlyMessage: 'Something went wrong revoking the API key',
    });

    return data;
};

export { getApiKeys, createApiKey, revokeApiKey };
